/**
 * Haus-Logik der Oberfläche: Geschoss-Bezeichnungen, Etagenliste, Vorlage für neue
 * Einheiten, Verbrauchszeiträume aus Ablesungen — und die Beispieldaten der Vorschau.
 *
 * Geschoss-Kodierung wie im Server (Migration 0025):
 *   -3 … -1 Untergeschosse, 0 Erdgeschoss, 1 … 60 Obergeschosse, 99 Dachgeschoss.
 */

import { standAm, type Vertragsverlauf, type Vertragswerte } from '@nkp/shared';

export const DACHGESCHOSS = 99;

export type BelegungStatus = 'vermietet' | 'leer' | 'eigennutzung';

export interface Belegung {
  status: BelegungStatus;
  mietverhaeltnisId: string | null;
  mieterName: string | null;
  beginn: string | null;
  ende: string | null;
  personenzahl: number | null;
  kaltmieteCent: number | null;
  vorauszahlungCent: number | null;
  leerSeit: string | null;
  naechsterEinzug: string | null;
}

export interface Einheit {
  id: string;
  objektId: string;
  bezeichnung: string;
  nutzungsart: 'wohnen' | 'gewerbe';
  wohnflaecheQm: number;
  zimmer: number | null;
  lage: string | null;
  megAnteil: number | null;
  eigennutzung: boolean;
  geschoss: number | null;
  position: string | null;
  reihenfolge: number;
}

export interface EinheitImHaus extends Einheit {
  belegung: Belegung;
  zaehlerAnzahl: number;
}

export interface Struktur {
  untergeschosse: number;
  obergeschosse: number | null;
  dachgeschoss: boolean;
}

export interface ObjektKopf extends Struktur {
  id: string;
  bezeichnung: string;
  /** efh, etw, mfh, wgh — fehlt bei älteren Objekten */
  objektart?: 'efh' | 'etw' | 'mfh' | 'wgh' | null;
  anschrift: string;
  gesamtflaecheQm: number;
  baujahr: number | null;
  mietspiegelQmCent: number | null;
  kappungsgrenzeProzent: number | null;
}

export interface HausKennzahlen {
  einheiten: number;
  vermietet: number;
  leer: number;
  eigennutzung: number;
  wohnflaecheQm: number;
  leerstandQm: number;
  sollKaltmieteCent: number;
  vorauszahlungCent: number;
}

export interface HausAntwort {
  objekt: ObjektKopf;
  einheiten: EinheitImHaus[];
  kennzahlen: HausKennzahlen;
}

export interface Ablesung {
  datum: string;
  stand: number;
  anlass: string;
}

export interface ZaehlerMitAblesungen {
  id: string;
  nummer: string;
  medium: string;
  einheit: string;
  fernablesbar: boolean;
  eingebautAm: string | null;
  ablesungen: Ablesung[];
}

export type KautionStatus = 'offen' | 'erhalten' | 'angelegt' | 'teilw_einbehalten' | 'zurueckgezahlt';
export type Mietart = 'normal' | 'staffel' | 'index';

export interface Mietverhaeltnis {
  id: string;
  einheitId: string;
  mieterId: string;
  mieterName: string;
  beginn: string;
  ende: string | null;
  kaltmieteCent: number;
  vzNebenkostenCent: number;
  vzHeizkostenCent: number;
  personenzahl: number;
  kautionCent: number;
  kautionStatus: KautionStatus;
  mietart: Mietart;
  letzteErhoehungAm: string | null;
  /** Vertragsänderungen ab Stichtag (Mieterhöhung, Vorauszahlung, Personen). */
  staende?: Vertragsaenderung[];
}

export type AenderungsGrund = 'vorauszahlung' | 'mieterhoehung' | 'personen' | 'sonstiges';

export const GRUND_LABEL: Record<AenderungsGrund, string> = {
  vorauszahlung: 'Anpassung der Vorauszahlungen',
  mieterhoehung: 'Mieterhöhung',
  personen: 'Änderung der Personenzahl',
  sonstiges: 'Sonstige Vertragsänderung',
};

export interface Vertragsaenderung {
  id: string;
  gueltigAb: string;
  kaltmieteCent: number;
  vzNebenkostenCent: number;
  vzHeizkostenCent: number;
  personenzahl: number;
  grund: AenderungsGrund;
}

/** Vertragsverlauf für den Rechenkern: Grundwerte ab Mietbeginn plus Stände. */
export function vertragsverlauf(m: Mietverhaeltnis): Vertragsverlauf {
  return {
    beginn: m.beginn,
    ende: m.ende,
    grundwerte: {
      kaltmieteCent: m.kaltmieteCent,
      vzNebenkostenCent: m.vzNebenkostenCent,
      vzHeizkostenCent: m.vzHeizkostenCent,
      personenzahl: m.personenzahl,
    },
    staende: (m.staende ?? []).map((x) => ({
      ab: x.gueltigAb,
      kaltmieteCent: x.kaltmieteCent,
      vzNebenkostenCent: x.vzNebenkostenCent,
      vzHeizkostenCent: x.vzHeizkostenCent,
      personenzahl: x.personenzahl,
    })),
  };
}

/**
 * Die an einem Tag gültigen Vertragswerte. Vor Mietbeginn gelten die Werte ab Beginn,
 * nach Mietende die zuletzt gültigen.
 */
export function werteAm(m: Mietverhaeltnis, datum: string): Vertragswerte {
  const tag = datum < m.beginn ? m.beginn : m.ende && datum > m.ende ? m.ende : datum;
  return standAm(vertragsverlauf(m), tag);
}

export interface AbrechnungsErgebnis {
  abrechnungId: string;
  zeitraumVon: string;
  zeitraumBis: string;
  status: string;
  mietverhaeltnisId: string;
  mieterName: string;
  kostenCent: number;
  /** Betriebskosten ohne Heizung/Warmwasser bzw. Heiz- und Warmwasserkosten */
  kostenNkCent?: number;
  kostenHeizungCent?: number;
  vorauszahlungCent: number;
  saldoCent: number;
  versendetAm: string | null;
}

export interface Leerstandskosten {
  abrechnungId: string;
  zeitraumVon: string;
  zeitraumBis: string;
  kostenCent: number;
}

export interface AkteAntwort {
  einheit: Einheit;
  objekt: ObjektKopf;
  mietverhaeltnisse: Mietverhaeltnis[];
  zaehler: ZaehlerMitAblesungen[];
  abrechnungen: AbrechnungsErgebnis[];
  leerstandskosten: Leerstandskosten[];
}

/* ------------------------------------------------------------------ Geschosse */

export function geschossKurz(g: number | null): string {
  if (g === null) return 'Ohne Etage';
  if (g === DACHGESCHOSS) return 'DG';
  if (g === 0) return 'EG';
  if (g === -1) return 'UG';
  if (g < 0) return `${-g}. UG`;
  return `${g}. OG`;
}

export function geschossLang(g: number | null): string {
  if (g === null) return 'Ohne Etagenzuordnung';
  if (g === DACHGESCHOSS) return 'Dachgeschoss';
  if (g === 0) return 'Erdgeschoss';
  if (g === -1) return 'Untergeschoss';
  if (g < 0) return `${-g}. Untergeschoss`;
  return `${g}. Obergeschoss`;
}

/** Etagen des Hauses von oben nach unten. */
export function etagen(s: Struktur): number[] {
  const liste: number[] = [];
  if (s.dachgeschoss) liste.push(DACHGESCHOSS);
  for (let g = s.obergeschosse ?? 0; g >= 1; g--) liste.push(g);
  liste.push(0);
  for (let g = 1; g <= s.untergeschosse; g++) liste.push(-g);
  return liste;
}

/** Lage auf der Etage je nach Anzahl der Einheiten. */
export function positionen(anzahl: number): Array<string | null> {
  if (anzahl <= 1) return [null];
  if (anzahl === 2) return ['links', 'rechts'];
  if (anzahl === 3) return ['links', 'mitte', 'rechts'];
  if (anzahl === 4) return ['links', 'mitte links', 'mitte rechts', 'rechts'];
  return Array.from({ length: anzahl }, (_, i) => `Nr. ${i + 1}`);
}

export interface VorlageEinheit {
  schluessel: string;
  bezeichnung: string;
  geschoss: number;
  position: string | null;
  reihenfolge: number;
  wohnflaecheQm: number | null;
  nutzungsart: 'wohnen' | 'gewerbe';
}

export type Benennung = 'lage' | 'nummer';

/**
 * Einheiten aus der Vorlage: je Etage `anzahl` Einheiten. Benennung nach Lage
 * („2. OG links“) oder durchnummeriert von unten nach oben („Whg. 1“, „Whg. 2“ …).
 */
export function einheitenAusVorlage(
  s: Struktur,
  jeEtage: Record<number, number>,
  benennung: Benennung,
): VorlageEinheit[] {
  const vonUnten = [...etagen(s)].reverse();
  const ergebnis: VorlageEinheit[] = [];
  let laufend = 0;
  for (const g of vonUnten) {
    const anzahl = Math.max(0, Math.min(20, jeEtage[g] ?? 0));
    const lagen = positionen(anzahl);
    for (let i = 0; i < anzahl; i++) {
      laufend += 1;
      const pos = lagen[i] ?? null;
      const bezeichnung =
        benennung === 'nummer' ? `Whg. ${laufend}` : [geschossKurz(g), pos].filter(Boolean).join(' ');
      ergebnis.push({
        schluessel: `${g}-${i}`,
        bezeichnung,
        geschoss: g,
        position: pos,
        reihenfolge: i + 1,
        wohnflaecheQm: null,
        nutzungsart: 'wohnen',
      });
    }
  }
  // Anzeige wie im Haus: oben nach unten, links nach rechts.
  return ergebnis.sort((a, b) => b.geschoss - a.geschoss || a.reihenfolge - b.reihenfolge);
}

/** Einheiten nach Etagen gruppieren (oben nach unten); Einheiten ohne Etage zuletzt. */
export function nachEtagen<T extends { geschoss: number | null; reihenfolge: number; bezeichnung: string }>(
  einheiten: T[],
  s: Struktur,
): Array<{ geschoss: number | null; einheiten: T[] }> {
  const gruppen = etagen(s).map((g) => ({
    geschoss: g as number | null,
    einheiten: einheiten
      .filter((e) => e.geschoss === g)
      .sort((a, b) => a.reihenfolge - b.reihenfolge || a.bezeichnung.localeCompare(b.bezeichnung, 'de')),
  }));
  // Einheiten auf Etagen außerhalb der Struktur (Altbestand) nicht verlieren.
  const bekannt = new Set(etagen(s));
  const ausserhalb = einheiten.filter((e) => e.geschoss !== null && !bekannt.has(e.geschoss));
  const ohne = einheiten.filter((e) => e.geschoss === null);
  const rest = [...ausserhalb, ...ohne];
  if (rest.length > 0) gruppen.push({ geschoss: null, einheiten: rest });
  return gruppen;
}

/* ------------------------------------------------------------------ Verbrauch */

export interface Verbrauchszeitraum {
  von: string;
  bis: string;
  tage: number;
  verbrauch: number;
  jeTag: number;
}

const TAG_MS = 24 * 60 * 60 * 1000;

/** Verbrauch zwischen aufeinanderfolgenden Ablesungen (neueste zuerst). */
export function verbrauchszeitraeume(ablesungen: Ablesung[]): Verbrauchszeitraum[] {
  const sortiert = [...ablesungen].sort((a, b) => a.datum.localeCompare(b.datum));
  const ergebnis: Verbrauchszeitraum[] = [];
  for (let i = 1; i < sortiert.length; i++) {
    const a = sortiert[i - 1];
    const b = sortiert[i];
    const tage = Math.round((Date.parse(b.datum) - Date.parse(a.datum)) / TAG_MS);
    if (tage <= 0) continue;
    const verbrauch = Math.round((b.stand - a.stand) * 1000) / 1000;
    ergebnis.push({ von: a.datum, bis: b.datum, tage, verbrauch, jeTag: verbrauch / tage });
  }
  return ergebnis.reverse();
}

/** Mietdauer in Jahren und Monaten, z. B. „5 Jahre, 6 Monate“. */
export function dauer(von: string, bis: string | null): string {
  const start = new Date(`${von}T00:00:00Z`);
  const ende = bis ? new Date(`${bis}T00:00:00Z`) : new Date();
  let monate =
    (ende.getUTCFullYear() - start.getUTCFullYear()) * 12 + (ende.getUTCMonth() - start.getUTCMonth());
  if (ende.getUTCDate() < start.getUTCDate()) monate -= 1;
  monate = Math.max(0, monate);
  const j = Math.floor(monate / 12);
  const m = monate % 12;
  const teile: string[] = [];
  if (j > 0) teile.push(`${j} ${j === 1 ? 'Jahr' : 'Jahre'}`);
  if (m > 0 || j === 0) teile.push(`${m} ${m === 1 ? 'Monat' : 'Monate'}`);
  return teile.join(', ');
}

export function heuteIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function tagDanach(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/* ------------------------------------------------------------------ Vorschau-Daten */

interface DemoMv {
  id: string;
  mieter: string;
  beginn: string;
  ende: string | null;
  personen: number;
  kaltCent: number;
  vzNkCent: number;
  vzHeizCent: number;
}

interface DemoEinheit {
  id: string;
  bezeichnung: string;
  geschoss: number;
  position: string | null;
  reihenfolge: number;
  qm: number;
  zimmer: number;
  eigennutzung?: boolean;
  nutzungsart?: 'wohnen' | 'gewerbe';
  mvs: DemoMv[];
}

interface DemoHaus {
  objekt: ObjektKopf;
  einheiten: DemoEinheit[];
}

const DEMO_HAEUSER: Record<string, DemoHaus> = {
  'obj-1': {
    objekt: {
      id: 'obj-1',
      bezeichnung: 'Lindenstraße 14',
      anschrift: 'Lindenstraße 14, 45127 Essen',
      gesamtflaecheQm: 412,
      baujahr: 1962,
      mietspiegelQmCent: 1180,
      kappungsgrenzeProzent: 20,
      untergeschosse: 1,
      obergeschosse: 2,
      dachgeschoss: false,
    },
    einheiten: [
      {
        id: 'e-1', bezeichnung: 'EG links', geschoss: 0, position: 'links', reihenfolge: 1, qm: 72, zimmer: 3,
        mvs: [{ id: 'mv-1', mieter: 'Katrin Berger', beginn: '2021-04-01', ende: null, personen: 2, kaltCent: 76000, vzNkCent: 17000, vzHeizCent: 9000 }],
      },
      {
        id: 'e-2', bezeichnung: 'EG rechts', geschoss: 0, position: 'rechts', reihenfolge: 2, qm: 65, zimmer: 2,
        mvs: [
          { id: 'mv-7', mieter: 'Sophie Arnold', beginn: '2026-11-01', ende: null, personen: 1, kaltCent: 74500, vzNkCent: 15500, vzHeizCent: 8000 },
          { id: 'mv-6', mieter: 'Peter Schulz', beginn: '2018-05-01', ende: '2026-07-31', personen: 1, kaltCent: 61000, vzNkCent: 14500, vzHeizCent: 7500 },
        ],
      },
      {
        id: 'e-3', bezeichnung: '1. OG links', geschoss: 1, position: 'links', reihenfolge: 1, qm: 72, zimmer: 3,
        mvs: [
          { id: 'mv-2', mieter: 'Murat Yildiz', beginn: '2023-09-15', ende: null, personen: 3, kaltCent: 82000, vzNkCent: 19500, vzHeizCent: 10500 },
          { id: 'mv-8', mieter: 'Helga Brandt', beginn: '2009-03-01', ende: '2023-08-31', personen: 1, kaltCent: 54000, vzNkCent: 15000, vzHeizCent: 8000 },
        ],
      },
      {
        id: 'e-4', bezeichnung: '1. OG rechts', geschoss: 1, position: 'rechts', reihenfolge: 2, qm: 65, zimmer: 2,
        mvs: [{ id: 'mv-4', mieter: 'Sabine Neumann', beginn: '2016-02-01', ende: null, personen: 1, kaltCent: 63000, vzNkCent: 15000, vzHeizCent: 8000 }],
      },
      {
        id: 'e-5', bezeichnung: '2. OG links', geschoss: 2, position: 'links', reihenfolge: 1, qm: 72, zimmer: 3,
        mvs: [{ id: 'mv-5', mieter: 'Jonas und Lea Krämer', beginn: '2022-08-01', ende: '2026-12-31', personen: 2, kaltCent: 79000, vzNkCent: 18000, vzHeizCent: 9500 }],
      },
      {
        id: 'e-6', bezeichnung: '2. OG rechts', geschoss: 2, position: 'rechts', reihenfolge: 2, qm: 66, zimmer: 3,
        mvs: [{ id: 'mv-9', mieter: 'Daniel Owusu', beginn: '2024-03-01', ende: null, personen: 4, kaltCent: 77500, vzNkCent: 21000, vzHeizCent: 11000 }],
      },
    ],
  },
  'obj-2': {
    objekt: {
      id: 'obj-2',
      bezeichnung: 'Am Wasserturm 3',
      anschrift: 'Am Wasserturm 3, 44135 Dortmund',
      gesamtflaecheQm: 214,
      baujahr: 1998,
      mietspiegelQmCent: 1050,
      kappungsgrenzeProzent: 15,
      untergeschosse: 0,
      obergeschosse: 1,
      dachgeschoss: true,
    },
    einheiten: [
      { id: 'e-21', bezeichnung: 'EG', geschoss: 0, position: null, reihenfolge: 1, qm: 78, zimmer: 3, eigennutzung: true, mvs: [] },
      {
        id: 'e-22', bezeichnung: '1. OG', geschoss: 1, position: null, reihenfolge: 1, qm: 76, zimmer: 3,
        mvs: [{ id: 'mv-21', mieter: 'Anna Petrov', beginn: '2020-06-01', ende: null, personen: 2, kaltCent: 79800, vzNkCent: 18000, vzHeizCent: 9000 }],
      },
      {
        id: 'e-23', bezeichnung: 'DG', geschoss: DACHGESCHOSS, position: null, reihenfolge: 1, qm: 60, zimmer: 2,
        mvs: [{ id: 'mv-3', mieter: 'Lena Hoffmann', beginn: '2019-01-01', ende: '2025-08-31', personen: 1, kaltCent: 58000, vzNkCent: 14500, vzHeizCent: 0 }],
      },
    ],
  },
};

function demoEinheit(e: DemoEinheit, objektId: string): Einheit {
  return {
    id: e.id,
    objektId,
    bezeichnung: e.bezeichnung,
    nutzungsart: e.nutzungsart ?? 'wohnen',
    wohnflaecheQm: e.qm,
    zimmer: e.zimmer,
    lage: null,
    megAnteil: null,
    eigennutzung: !!e.eigennutzung,
    geschoss: e.geschoss,
    position: e.position,
    reihenfolge: e.reihenfolge,
  };
}

function demoBelegung(e: DemoEinheit): Belegung {
  const heute = heuteIso();
  const aktiv = e.mvs.find((m) => m.beginn <= heute && (m.ende === null || m.ende >= heute));
  const vergangen = e.mvs.filter((m) => m.ende !== null && m.ende < heute).map((m) => m.ende as string);
  const kuenftig = e.mvs.filter((m) => m.beginn > heute).map((m) => m.beginn);
  return {
    status: aktiv ? 'vermietet' : e.eigennutzung ? 'eigennutzung' : 'leer',
    mietverhaeltnisId: aktiv?.id ?? null,
    mieterName: aktiv?.mieter ?? null,
    beginn: aktiv?.beginn ?? null,
    ende: aktiv?.ende ?? null,
    personenzahl: aktiv?.personen ?? null,
    kaltmieteCent: aktiv?.kaltCent ?? null,
    vorauszahlungCent: aktiv ? aktiv.vzNkCent + aktiv.vzHeizCent : null,
    leerSeit: aktiv ? null : (vergangen.sort().pop() ?? null),
    naechsterEinzug: kuenftig.sort()[0] ?? null,
  };
}

export function demoHausListe(): Array<{ id: string; vermietet: number; eigennutzung: number; einheiten: number; struktur: Struktur }> {
  const ids = new Set([...Object.keys(DEMO_HAEUSER), ...demoSpeicher.keys()]);
  return [...ids]
    .map((id) => demoHaus(id))
    .filter((h): h is HausAntwort => h !== null)
    .map((h) => ({
      id: h.objekt.id,
      einheiten: h.kennzahlen.einheiten,
      vermietet: h.kennzahlen.vermietet,
      eigennutzung: h.kennzahlen.eigennutzung,
      struktur: h.objekt,
    }));
}

/** Änderungen der Vorschau (Assistent, neue Einheiten) für die Dauer der Sitzung. */
const demoSpeicher = new Map<string, HausAntwort>();

export function demoHausSpeichern(h: HausAntwort): void {
  demoSpeicher.set(h.objekt.id, h);
}

export function demoHaus(objektId: string): HausAntwort | null {
  const gespeichert = demoSpeicher.get(objektId);
  if (gespeichert) return gespeichert;
  const h = DEMO_HAEUSER[objektId];
  if (!h) return null;
  const einheiten: EinheitImHaus[] = h.einheiten.map((e) => ({
    ...demoEinheit(e, objektId),
    belegung: demoBelegung(e),
    zaehlerAnzahl: 3,
  }));
  return { objekt: h.objekt, einheiten, kennzahlen: kennzahlenAus(einheiten) };
}

/** Vorschau-Akte: Zählerstände und Abrechnungsergebnisse deterministisch aus der Einheit abgeleitet. */
export function demoAkte(einheitId: string): AkteAntwort | null {
  for (const h of Object.values(DEMO_HAEUSER)) {
    const e = h.einheiten.find((x) => x.id === einheitId);
    if (!e) continue;
    const faktor = e.qm / 70;
    const stichtage = ['2023-12-31', '2024-12-31', '2025-12-31'];
    const wechsel = e.mvs
      .map((m) => m.ende)
      .filter((d): d is string => !!d && d > '2023-12-31' && d < '2026-01-01');
    const zaehler: ZaehlerMitAblesungen[] = [
      { medium: 'kaltwasser', einheit: 'm³', basis: 412, jahr: 38 },
      { medium: 'warmwasser', einheit: 'm³', basis: 186, jahr: 17 },
      { medium: 'waerme', einheit: 'kWh', basis: 48210, jahr: 6900 },
    ].map((z, i) => {
      const daten = [...stichtage, ...wechsel].sort();
      let stand = z.basis * faktor;
      let vorher = daten[0];
      const ablesungen: Ablesung[] = daten.map((d, j) => {
        if (j > 0) {
          const tage = (Date.parse(d) - Date.parse(vorher)) / TAG_MS;
          stand += (z.jahr * faktor * tage) / 365 * (1 + ((j + i) % 3) * 0.04);
          vorher = d;
        }
        return {
          datum: d,
          stand: Math.round(stand * (z.medium === 'waerme' ? 1 : 1000)) / (z.medium === 'waerme' ? 1 : 1000),
          anlass: wechsel.includes(d) ? 'auszug' : 'periodisch',
        };
      });
      return {
        id: `${einheitId}-z${i}`,
        nummer: `${z.medium === 'waerme' ? 'WMZ' : z.medium === 'kaltwasser' ? 'KW' : 'WW'}-${e.bezeichnung.replace(/\W+/g, '')}`,
        medium: z.medium,
        einheit: z.einheit,
        fernablesbar: z.medium === 'waerme',
        eingebautAm: '2019-06-01',
        ablesungen,
      };
    });

    const abrechnungen: AbrechnungsErgebnis[] = [];
    const leerstandskosten: Leerstandskosten[] = [];
    for (const jahr of [2025, 2024]) {
      const von = `${jahr}-01-01`;
      const bis = `${jahr}-12-31`;
      // Rund 51 €/m² und Jahr Betriebs- und Heizkosten, je Einheit leicht unterschiedlich.
      const jahresKosten = Math.round(e.qm * (4900 + (e.zimmer % 3) * 180) * (jahr === 2025 ? 1.03 : 1));
      let belegteTage = 0;
      for (const m of e.mvs) {
        const s = m.beginn > von ? m.beginn : von;
        const en = m.ende && m.ende < bis ? m.ende : bis;
        if (s > en) continue;
        const tage = Math.round((Date.parse(en) - Date.parse(s)) / TAG_MS) + 1;
        belegteTage += tage;
        const kosten = Math.round((jahresKosten * tage) / 365);
        const monate = Math.round(tage / 30.4);
        const vz = (m.vzNkCent + m.vzHeizCent) * monate;
        abrechnungen.push({
          abrechnungId: `abr-${h.objekt.id}-${jahr}`,
          zeitraumVon: von,
          zeitraumBis: bis,
          status: jahr === 2025 ? 'berechnet' : 'versendet',
          mietverhaeltnisId: m.id,
          mieterName: m.mieter,
          kostenCent: kosten,
          kostenNkCent: Math.round(kosten * 0.62),
          kostenHeizungCent: kosten - Math.round(kosten * 0.62),
          vorauszahlungCent: vz,
          saldoCent: kosten - vz,
          versendetAm: jahr === 2024 ? '2025-05-14' : null,
        });
      }
      if (belegteTage < 365 && !e.eigennutzung) {
        leerstandskosten.push({
          abrechnungId: `abr-${h.objekt.id}-${jahr}`,
          zeitraumVon: von,
          zeitraumBis: bis,
          kostenCent: Math.round((jahresKosten * (365 - belegteTage)) / 365),
        });
      }
    }

    return {
      einheit: demoEinheit(e, h.objekt.id),
      objekt: h.objekt,
      mietverhaeltnisse: [...e.mvs]
        .sort((a, b) => b.beginn.localeCompare(a.beginn))
        .map((m) => ({
          id: m.id,
          einheitId: e.id,
          mieterId: `m-${m.id}`,
          mieterName: m.mieter,
          beginn: m.beginn,
          ende: m.ende,
          kaltmieteCent: m.kaltCent,
          vzNebenkostenCent: m.vzNkCent,
          vzHeizkostenCent: m.vzHeizCent,
          personenzahl: m.personen,
          kautionCent: m.kaltCent * 3,
          kautionStatus: m.ende && m.ende < heuteIso() ? 'zurueckgezahlt' : 'angelegt',
          mietart: 'normal',
          letzteErhoehungAm: null,
        })),
      zaehler: e.eigennutzung ? zaehler.slice(0, 1) : zaehler,
      abrechnungen,
      leerstandskosten,
    };
  }
  // In der Vorschau neu angelegte Einheit: leere Akte.
  for (const h of demoSpeicher.values()) {
    const e = h.einheiten.find((x) => x.id === einheitId);
    if (e) {
      return { einheit: e, objekt: h.objekt, mietverhaeltnisse: [], zaehler: [], abrechnungen: [], leerstandskosten: [] };
    }
  }
  return null;
}

/** Kennzahlen aus den Einheiten neu bilden (Vorschau nach lokalen Änderungen). */
export function kennzahlenAus(einheiten: EinheitImHaus[]): HausKennzahlen {
  const k: HausKennzahlen = {
    einheiten: einheiten.length,
    vermietet: 0,
    leer: 0,
    eigennutzung: 0,
    wohnflaecheQm: 0,
    leerstandQm: 0,
    sollKaltmieteCent: 0,
    vorauszahlungCent: 0,
  };
  for (const e of einheiten) {
    k.wohnflaecheQm += e.wohnflaecheQm;
    if (e.belegung.status === 'vermietet') {
      k.vermietet += 1;
      k.sollKaltmieteCent += e.belegung.kaltmieteCent ?? 0;
      k.vorauszahlungCent += e.belegung.vorauszahlungCent ?? 0;
    } else if (e.belegung.status === 'eigennutzung') k.eigennutzung += 1;
    else {
      k.leer += 1;
      k.leerstandQm += e.wohnflaecheQm;
    }
  }
  k.wohnflaecheQm = Math.round(k.wohnflaecheQm * 100) / 100;
  k.leerstandQm = Math.round(k.leerstandQm * 100) / 100;
  return k;
}

export function leereBelegung(): Belegung {
  return {
    status: 'leer',
    mietverhaeltnisId: null,
    mieterName: null,
    beginn: null,
    ende: null,
    personenzahl: null,
    kaltmieteCent: null,
    vorauszahlungCent: null,
    leerSeit: null,
    naechsterEinzug: null,
  };
}
